import React, { useState, useEffect, useContext } from 'react';
import { Card, Button, Form, Row, Col, Spinner, Badge, Tab, Tabs, Table, Modal, Alert, ProgressBar } from 'react-bootstrap';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaKey, FaPlus, FaClock, FaCheckCircle, FaExclamationTriangle, FaCog } from 'react-icons/fa';
import AuthContext from '../../context/AuthContext';

const LICENSE_OPTIONS = [
  { value: 'TRIAL', label: 'Trial', days: 30 },
  { value: 'STANDARD', label: 'Standard', days: 90 },
  { value: 'HALFYEAR', label: 'Half Year', days: 180 },
  { value: 'YEARLY', label: 'Full Year', days: 365 }
];

const CustomerDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { token } = useContext(AuthContext);
  
  const [customer, setCustomer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [users, setUsers] = useState([]);
  const [showResetModal, setShowResetModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [requirePasswordChange, setRequirePasswordChange] = useState(true);
  const [isResetting, setIsResetting] = useState(false);
  
  // License management state
  const [showLicenseModal, setShowLicenseModal] = useState(false);
  const [licenseAction, setLicenseAction] = useState('create'); // 'create' or 'renew'
  const [selectedLicenseType, setSelectedLicenseType] = useState('STANDARD');
  const [customDays, setCustomDays] = useState('');
  const [isProcessingLicense, setIsProcessingLicense] = useState(false);
  const [licenseHistory, setLicenseHistory] = useState([]);
  
  // Feature flags state
  const [features, setFeatures] = useState({ receiptScanning: false });
  const [isSavingFeatures, setIsSavingFeatures] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    companyName: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    phone: '',
    email: '',
    companySlogan: '',
    ownerName: '',
    companyPhone: '',
    companyEmail: '',
    companyAddress: '',
    companyCity: '',
    companyState: '',
    companyZipCode: '',
    licenseNumber1: '',
    licenseNumber2: '',
    licenseNumber3: '',
    isActive: true
  });

  useEffect(() => {
    fetchCustomer();
    fetchCustomerUsers();
    fetchLicenseHistory();
    fetchFeatures();
  }, [id, token]);

  const fetchCustomer = async () => {
    try {
      const response = await fetch(`/api/customers/${id}`, {
        headers: { 'x-auth-token': token }
      });

      if (!response.ok) throw new Error('Failed to fetch customer');

      const data = await response.json();
      setCustomer(data);
      setFormData({
        companyName: data.CompanyName || '',
        address: data.Address || '',
        city: data.City || '',
        state: data.State || '',
        zipCode: data.ZipCode || '',
        phone: data.Phone || '',
        email: data.Email || '',
        companySlogan: data.CompanySlogan || '',
        ownerName: data.OwnerName || '',
        companyPhone: data.CompanyPhone || '',
        companyEmail: data.CompanyEmail || '',
        companyAddress: data.CompanyAddress || '',
        companyCity: data.CompanyCity || '',
        companyState: data.CompanyState || '',
        companyZipCode: data.CompanyZipCode || '',
        licenseNumber1: data.LicenseNumber1 || '',
        licenseNumber2: data.LicenseNumber2 || '',
        licenseNumber3: data.LicenseNumber3 || '',
        isActive: data.IsActive !== false
      });
    } catch (err) {
      console.error('Error fetching customer:', err);
      toast.error('Failed to load customer details');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCustomerUsers = async () => {
    try {
      const response = await fetch(`/api/admin/customers/${id}/users`, {
        headers: { 'x-auth-token': token }
      });

      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      console.error('Error fetching customer users:', err);
    }
  };

  const fetchLicenseHistory = async () => {
    try {
      const response = await fetch(`/api/license/admin/history/${id}`, {
        headers: { 'x-auth-token': token }
      });

      if (response.ok) {
        const data = await response.json();
        setLicenseHistory(data);
      }
    } catch (err) {
      console.error('Error fetching license history:', err);
    }
  };

  const fetchFeatures = async () => {
    try {
      const response = await fetch(`/api/admin/customers/${id}/features`, {
        headers: { 'x-auth-token': token }
      });

      if (response.ok) {
        const data = await response.json();
        setFeatures(data.features || { receiptScanning: false });
      }
    } catch (err) {
      console.error('Error fetching features:', err);
    }
  };

  const handleFeatureToggle = async (featureName, enabled) => {
    setIsSavingFeatures(true);
    
    try {
      const response = await fetch(`/api/admin/customers/${id}/features`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          features: { [featureName]: enabled }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to update features');
      }

      const data = await response.json();
      setFeatures(data.features);
      toast.success(`Feature ${enabled ? 'enabled' : 'disabled'} successfully`);
    } catch (err) {
      console.error('Error updating features:', err);
      toast.error('Failed to update feature');
    } finally {
      setIsSavingFeatures(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      const response = await fetch(`/api/customers/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to update customer');
      }

      const updatedCustomer = await response.json();
      setCustomer(updatedCustomer);
      setIsEditing(false);
      toast.success('Customer updated successfully');
    } catch (err) {
      console.error('Error updating customer:', err);
      toast.error(err.message || 'Failed to update customer');
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetPassword = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setIsResetting(true);
    try {
      const response = await fetch(`/api/admin/customers/${id}/users/${selectedUser.UserId}/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify({
          newPassword,
          requirePasswordChange
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.msg || 'Failed to reset password');
      }

      toast.success(`Password reset for ${selectedUser.Username}. ${requirePasswordChange ? 'They will be prompted to change it on next login.' : ''}`);
      setShowResetModal(false);
      setNewPassword('');
      setSelectedUser(null);
      fetchCustomerUsers();
    } catch (err) {
      console.error('Error resetting password:', err);
      toast.error(err.message || 'Failed to reset password');
    } finally {
      setIsResetting(false);
    }
  };

  const openResetModal = (user) => {
    setSelectedUser(user);
    setNewPassword('');
    setRequirePasswordChange(true);
    setShowResetModal(true);
  };

  const openLicenseModal = (action) => {
    setLicenseAction(action);
    setSelectedLicenseType('STANDARD');
    setCustomDays('');
    setShowLicenseModal(true);
  };

  const handleLicenseAction = async () => {
    setIsProcessingLicense(true);
    
    try {
      const selectedOption = LICENSE_OPTIONS.find(opt => opt.value === selectedLicenseType);
      const days = customDays ? parseInt(customDays) : selectedOption.days;
      
      let endpoint, body;
      
      if (licenseAction === 'create') {
        endpoint = '/api/license/admin/create';
        body = {
          customerId: parseInt(id),
          licenseType: selectedLicenseType,
          daysValid: days
        };
      } else {
        endpoint = '/api/license/admin/renew';
        body = {
          customerId: parseInt(id),
          additionalDays: days
        };
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token
        },
        body: JSON.stringify(body)
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to process license');
      }

      toast.success(licenseAction === 'create' 
        ? `License created! Expires: ${new Date(data.expirationDate).toLocaleDateString()}`
        : `License renewed! New expiration: ${new Date(data.newExpiration).toLocaleDateString()}`
      );
      
      setShowLicenseModal(false);
      fetchCustomer();
      fetchLicenseHistory();
    } catch (err) {
      console.error('Error processing license:', err);
      toast.error(err.message || 'Failed to process license');
    } finally {
      setIsProcessingLicense(false);
    }
  };

  const getLicenseStatus = () => {
    if (!customer?.LicenseExpiration) return { status: 'NO_LICENSE', color: 'warning' };
    
    const expDate = new Date(customer.LicenseExpiration);
    const now = new Date();
    const daysRemaining = Math.ceil((expDate - now) / (1000 * 60 * 60 * 24));
    
    if (daysRemaining <= 0) return { status: 'EXPIRED', color: 'danger', daysRemaining: 0 };
    if (daysRemaining <= 7) return { status: 'EXPIRING_SOON', color: 'danger', daysRemaining };
    if (daysRemaining <= 14) return { status: 'EXPIRING_SOON', color: 'warning', daysRemaining };
    return { status: 'ACTIVE', color: 'success', daysRemaining };
  };

  if (isLoading) {
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
        <p>Loading customer details...</p>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center mt-5">
        <p>Customer not found</p>
        <Button as={Link} to="/admin/customers" variant="primary">
          Back to Customers
        </Button>
      </div>
    );
  }

  const licenseStatus = getLicenseStatus();

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>
          {customer.CompanyName}
          <Badge bg={customer.IsActive ? 'success' : 'danger'} className="ms-3">
            {customer.IsActive ? 'Active' : 'Inactive'}
          </Badge>
        </h2>
        <div>
          {!isEditing && (
            <Button variant="primary" onClick={() => setIsEditing(true)} className="me-2">
              Edit Customer
            </Button>
          )}
          <Button as={Link} to="/admin/customers" variant="secondary">
            Back to Customers
          </Button>
        </div>
      </div>

      <Tabs defaultActiveKey="details" className="mb-4">
        <Tab eventKey="details" title="Customer Details">
          <Card>
            <Card.Body>
              {isEditing ? (
                <Form onSubmit={handleSubmit}>
                  {/* Basic Information */}
                  <h5 className="mb-3">Basic Information</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Company Name *</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyName"
                          value={formData.companyName}
                          onChange={handleChange}
                          required
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Owner Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="ownerName"
                          value={formData.ownerName}
                          onChange={handleChange}
                          placeholder="e.g., John Smith"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Company Slogan</Form.Label>
                        <Form.Control
                          type="text"
                          name="companySlogan"
                          value={formData.companySlogan}
                          onChange={handleChange}
                          placeholder="e.g., Quality Work, Every Time"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Contact Information */}
                  <h5 className="mb-3 mt-4">Contact Information</h5>
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Phone</Form.Label>
                        <Form.Control
                          type="text"
                          name="phone"
                          value={formData.phone}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={formData.email}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Address</Form.Label>
                        <Form.Control
                          type="text"
                          name="address"
                          value={formData.address}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="city"
                          value={formData.city}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          name="state"
                          value={formData.state}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="zipCode"
                          value={formData.zipCode}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Company Profile for Documents */}
                  <h5 className="mb-3 mt-4">Company Profile (for Proposals/Invoices)</h5>
                  <p className="text-muted small">These fields will appear on generated documents. Leave blank to use the contact info above.</p>
                  
                  <Row className="mb-3">
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Company Phone (for documents)</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyPhone"
                          value={formData.companyPhone}
                          onChange={handleChange}
                          placeholder="Leave blank to use contact phone"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group>
                        <Form.Label>Company Email (for documents)</Form.Label>
                        <Form.Control
                          type="email"
                          name="companyEmail"
                          value={formData.companyEmail}
                          onChange={handleChange}
                          placeholder="Leave blank to use contact email"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Company Address (for documents)</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyAddress"
                          value={formData.companyAddress}
                          onChange={handleChange}
                          placeholder="Leave blank to use contact address"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>City</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyCity"
                          value={formData.companyCity}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>State</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyState"
                          value={formData.companyState}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Zip Code</Form.Label>
                        <Form.Control
                          type="text"
                          name="companyZipCode"
                          value={formData.companyZipCode}
                          onChange={handleChange}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Contractor License Numbers */}
                  <h5 className="mb-3 mt-4">Contractor License Numbers</h5>
                  <p className="text-muted small">License numbers that appear on proposals and invoices.</p>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>License #1</Form.Label>
                        <Form.Control
                          type="text"
                          name="licenseNumber1"
                          value={formData.licenseNumber1}
                          onChange={handleChange}
                          placeholder="e.g., CS-010214"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>License #2</Form.Label>
                        <Form.Control
                          type="text"
                          name="licenseNumber2"
                          value={formData.licenseNumber2}
                          onChange={handleChange}
                          placeholder="e.g., HIC-204662"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>License #3</Form.Label>
                        <Form.Control
                          type="text"
                          name="licenseNumber3"
                          value={formData.licenseNumber3}
                          onChange={handleChange}
                          placeholder="e.g., INS-123456"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  {/* Status */}
                  <h5 className="mb-3 mt-4">Status</h5>
                  <Form.Group className="mb-3">
                    <Form.Check
                      type="checkbox"
                      name="isActive"
                      label="Customer is active"
                      checked={formData.isActive}
                      onChange={handleChange}
                    />
                  </Form.Group>

                  <div className="d-flex gap-2">
                    <Button type="submit" variant="success" disabled={isSaving}>
                      {isSaving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button variant="secondary" onClick={() => setIsEditing(false)}>
                      Cancel
                    </Button>
                  </div>
                </Form>
              ) : (
                /* View Mode */
                <div>
                  <h5 className="mb-3">Basic Information</h5>
                  <Row className="mb-2">
                    <Col md={3}><strong>Company Name:</strong></Col>
                    <Col md={9}>{customer.CompanyName}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={3}><strong>Owner Name:</strong></Col>
                    <Col md={9}>{customer.OwnerName || '-'}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={3}><strong>Slogan:</strong></Col>
                    <Col md={9}>{customer.CompanySlogan || '-'}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={3}><strong>Database:</strong></Col>
                    <Col md={9}><code>{customer.DatabaseName}</code></Col>
                  </Row>

                  <h5 className="mb-3 mt-4">Contact Information</h5>
                  <Row className="mb-2">
                    <Col md={3}><strong>Phone:</strong></Col>
                    <Col md={9}>{customer.Phone || '-'}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={3}><strong>Email:</strong></Col>
                    <Col md={9}>{customer.Email || '-'}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={3}><strong>Address:</strong></Col>
                    <Col md={9}>
                      {customer.Address ? (
                        <>
                          {customer.Address}<br />
                          {customer.City}, {customer.State} {customer.ZipCode}
                        </>
                      ) : '-'}
                    </Col>
                  </Row>

                  <h5 className="mb-3 mt-4">System Information</h5>
                  <Row className="mb-2">
                    <Col md={3}><strong>Created:</strong></Col>
                    <Col md={9}>{new Date(customer.CreatedAt).toLocaleString()}</Col>
                  </Row>
                  <Row className="mb-2">
                    <Col md={3}><strong>Last Updated:</strong></Col>
                    <Col md={9}>{customer.UpdatedAt ? new Date(customer.UpdatedAt).toLocaleString() : '-'}</Col>
                  </Row>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="license" title={
          <span>
            <FaKey className="me-1" />
            Software License
            {licenseStatus.status !== 'ACTIVE' && (
              <Badge bg={licenseStatus.color} className="ms-2" pill>!</Badge>
            )}
          </span>
        }>
          <Card>
            <Card.Body>
              {/* Current License Status */}
              <div className="mb-4">
                <h5 className="d-flex align-items-center">
                  License Status
                  <Badge bg={licenseStatus.color} className="ms-2">
                    {licenseStatus.status === 'ACTIVE' && <><FaCheckCircle className="me-1" />Active</>}
                    {licenseStatus.status === 'EXPIRING_SOON' && <><FaClock className="me-1" />Expiring Soon</>}
                    {licenseStatus.status === 'EXPIRED' && <><FaExclamationTriangle className="me-1" />Expired</>}
                    {licenseStatus.status === 'NO_LICENSE' && <><FaKey className="me-1" />No License</>}
                  </Badge>
                </h5>
                
                {customer.LicenseKey ? (
                  <Card className="bg-light">
                    <Card.Body>
                      <Row>
                        <Col md={4}>
                          <strong>License Type:</strong><br />
                          <Badge bg="primary">{customer.LicenseType || 'Standard'}</Badge>
                        </Col>
                        <Col md={4}>
                          <strong>License Key:</strong><br />
                          <code>{customer.LicenseKey}</code>
                        </Col>
                        <Col md={4}>
                          <strong>Expiration:</strong><br />
                          {new Date(customer.LicenseExpiration).toLocaleDateString()}
                          {licenseStatus.daysRemaining !== undefined && (
                            <span className={`ms-2 text-${licenseStatus.color}`}>
                              ({licenseStatus.daysRemaining} days remaining)
                            </span>
                          )}
                        </Col>
                      </Row>
                      
                      {licenseStatus.daysRemaining > 0 && (
                        <div className="mt-3">
                          <ProgressBar 
                            now={Math.min(100, (licenseStatus.daysRemaining / 365) * 100)} 
                            variant={licenseStatus.color}
                            style={{ height: '8px' }}
                          />
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                ) : (
                  <Alert variant="warning">
                    <FaExclamationTriangle className="me-2" />
                    No license assigned to this customer. Click "Create License" to assign one.
                  </Alert>
                )}
              </div>
              
              {/* License Actions */}
              <div className="mb-4">
                <h5>License Actions</h5>
                <div className="d-flex gap-2">
                  {!customer.LicenseKey ? (
                    <Button variant="success" onClick={() => openLicenseModal('create')}>
                      <FaPlus className="me-1" /> Create License
                    </Button>
                  ) : (
                    <>
                      <Button variant="primary" onClick={() => openLicenseModal('renew')}>
                        <FaClock className="me-1" /> Extend License
                      </Button>
                      <Button variant="success" onClick={() => openLicenseModal('create')}>
                        <FaPlus className="me-1" /> New License
                      </Button>
                    </>
                  )}
                </div>
              </div>
              
              {/* License History */}
              {licenseHistory.length > 0 && (
                <div>
                  <h5>License History</h5>
                  <Table striped hover size="sm">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Action</th>
                        <th>Details</th>
                        <th>By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {licenseHistory.map((entry, idx) => (
                        <tr key={idx}>
                          <td>{new Date(entry.CreatedAt).toLocaleString()}</td>
                          <td>
                            <Badge bg={
                              entry.Action === 'CREATED' ? 'success' :
                              entry.Action === 'RENEWED' ? 'primary' :
                              entry.Action === 'REVOKED' ? 'danger' : 'secondary'
                            }>
                              {entry.Action}
                            </Badge>
                          </td>
                          <td>
                            {entry.NewExpirationDate && (
                              <span>Expires: {new Date(entry.NewExpirationDate).toLocaleDateString()}</span>
                            )}
                            {entry.Notes && <span className="text-muted ms-2">({entry.Notes})</span>}
                          </td>
                          <td>{entry.PerformedBy}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="users" title="Users">
          <Card>
            <Card.Body>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0">Customer Users</h5>
              </div>
              
              {users.length > 0 ? (
                <Table striped hover>
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Role</th>
                      <th>Password Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map(user => (
                      <tr key={user.UserId}>
                        <td>{user.Username}</td>
                        <td>{user.FirstName} {user.LastName}</td>
                        <td>{user.Email}</td>
                        <td>
                          <Badge bg={user.Role === 'admin' ? 'primary' : 'secondary'}>
                            {user.Role}
                          </Badge>
                        </td>
                        <td>
                          {user.RequirePasswordChange ? (
                            <Badge bg="warning" text="dark">Must Change</Badge>
                          ) : (
                            <Badge bg="success">OK</Badge>
                          )}
                        </td>
                        <td>
                          <Button
                            variant="outline-warning"
                            size="sm"
                            onClick={() => openResetModal(user)}
                          >
                            Reset Password
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No users found for this customer.</p>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="features" title={
          <span>
            <FaCog className="me-1" />
            Premium Features
          </span>
        }>
          <Card>
            <Card.Body>
              <h5 className="mb-4">Premium Feature Management</h5>
              <p className="text-muted mb-4">
                Enable or disable premium features for this customer. These features may incur additional costs.
              </p>
              
              <Card className="mb-3">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1">
                        📷 AI Receipt Scanning
                        {features.receiptScanning && (
                          <Badge bg="success" className="ms-2">Enabled</Badge>
                        )}
                      </h6>
                      <p className="text-muted mb-0 small">
                        Allows users to scan receipts with their phone camera. AI automatically extracts 
                        items, prices, and store information to populate materials. 
                        <br />
                        <strong>Cost:</strong> ~$0.02-0.05 per scan (billed to you)
                      </p>
                    </div>
                    <div>
                      <Form.Check
                        type="switch"
                        id="receipt-scanning-switch"
                        checked={features.receiptScanning || false}
                        onChange={(e) => handleFeatureToggle('receiptScanning', e.target.checked)}
                        disabled={isSavingFeatures}
                        label=""
                        style={{ transform: 'scale(1.5)' }}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              {/* Placeholder for future features */}
              <Card className="mb-3 bg-light">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 text-muted">
                        🤖 AI Assistant
                        <Badge bg="secondary" className="ms-2">Coming Soon</Badge>
                      </h6>
                      <p className="text-muted mb-0 small">
                        Voice and text commands for hands-free data entry and project queries.
                      </p>
                    </div>
                    <div>
                      <Form.Check
                        type="switch"
                        disabled
                        label=""
                        style={{ transform: 'scale(1.5)', opacity: 0.5 }}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
              
              <Card className="mb-3 bg-light">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-center">
                    <div>
                      <h6 className="mb-1 text-muted">
                        📱 Mobile App Access
                        <Badge bg="secondary" className="ms-2">Coming Soon</Badge>
                      </h6>
                      <p className="text-muted mb-0 small">
                        Native mobile app for iOS and Android with offline support.
                      </p>
                    </div>
                    <div>
                      <Form.Check
                        type="switch"
                        disabled
                        label=""
                        style={{ transform: 'scale(1.5)', opacity: 0.5 }}
                      />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>

      {/* Reset Password Modal */}
      <Modal show={showResetModal} onHide={() => setShowResetModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Reset Password</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedUser && (
            <>
              <p>Reset password for <strong>{selectedUser.Username}</strong> ({selectedUser.Email})</p>
              
              <Form.Group className="mb-3">
                <Form.Label>New Password</Form.Label>
                <Form.Control
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new temporary password"
                  minLength={6}
                />
                <Form.Text className="text-muted">
                  Minimum 6 characters
                </Form.Text>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Require user to change password on next login"
                  checked={requirePasswordChange}
                  onChange={(e) => setRequirePasswordChange(e.target.checked)}
                />
              </Form.Group>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowResetModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="warning" 
            onClick={handleResetPassword}
            disabled={isResetting || !newPassword || newPassword.length < 6}
          >
            {isResetting ? 'Resetting...' : 'Reset Password'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* License Action Modal */}
      <Modal show={showLicenseModal} onHide={() => setShowLicenseModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>
            <FaKey className="me-2" />
            {licenseAction === 'create' ? 'Create New License' : 'Extend License'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>License Type</Form.Label>
            <Form.Select
              value={selectedLicenseType}
              onChange={(e) => setSelectedLicenseType(e.target.value)}
            >
              {LICENSE_OPTIONS.map(opt => (
                <option key={opt.value} value={opt.value}>
                  {opt.label} - {opt.days} Days
                </option>
              ))}
            </Form.Select>
          </Form.Group>
          
          <Form.Group className="mb-3">
            <Form.Label>Custom Days (optional)</Form.Label>
            <Form.Control
              type="number"
              value={customDays}
              onChange={(e) => setCustomDays(e.target.value)}
              placeholder={`Default: ${LICENSE_OPTIONS.find(o => o.value === selectedLicenseType)?.days} days`}
              min="1"
            />
            <Form.Text className="text-muted">
              Leave blank to use the default duration for the selected license type
            </Form.Text>
          </Form.Group>
          
          <Alert variant="info">
            {licenseAction === 'create' ? (
              <>
                <strong>Creating new license:</strong> This will replace any existing license.
                The new license will be valid for{' '}
                <strong>{customDays || LICENSE_OPTIONS.find(o => o.value === selectedLicenseType)?.days} days</strong>{' '}
                starting today.
              </>
            ) : (
              <>
                <strong>Extending license:</strong> This will add{' '}
                <strong>{customDays || LICENSE_OPTIONS.find(o => o.value === selectedLicenseType)?.days} days</strong>{' '}
                to the current expiration date.
              </>
            )}
          </Alert>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowLicenseModal(false)}>
            Cancel
          </Button>
          <Button 
            variant="primary" 
            onClick={handleLicenseAction}
            disabled={isProcessingLicense}
          >
            {isProcessingLicense ? 'Processing...' : (licenseAction === 'create' ? 'Create License' : 'Extend License')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CustomerDetail;
